import { FC, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { FeatureFlag } from '@/_shared/constants/featureFlags';
import { useFeatureFlags } from '@/_shared/hooks/useFeatureFlag';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';

const SECTIONS = [
  {
    key: 'measurement',
    items: ['measurementSoftSkills', 'measurementHardSkills', 'measurementOnDemand'],
  },
  {
    key: 'coaching',
    items: ['coachingHr', 'coachingOneToOne'],
  },
  {
    key: 'training',
    items: ['trainingCatalog', 'trainingCustom'],
  },
  {
    key: 'aiBoost',
    items: ['aiBoostFeatures'],
  },
] as const;

export const IncludedInPlan: FC = () => {
  // eslint-disable-next-line @altermaind/use-translations-scope
  const t = useTranslations('settings.tabs.plan.included');
  const { genericResponsiveValue } = useResponsiveStyle();
  const { isFeatureEnabled } = useFeatureFlags();
  const hideAiBoost = isFeatureEnabled(FeatureFlag.HIDE_FEATURE_AI_BOOST_INCLUDED_IN_PLAN);
  const gap = parseFloat(`${useThemeValue('sizes', 'xs')}`);

  const visibleSections = useMemo(
    () => SECTIONS.filter(section => !(section.key === 'aiBoost' && hideAiBoost)),
    [hideAiBoost],
  );

  return (
    <>
      <Box mb='s'>
        <Typography variant='p2' color='grey/light'>
          {t('title')}
        </Typography>
      </Box>
      <Box
        display='flex'
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        style={{ gap }}
      >
        {visibleSections.map(section => (
          <Box key={section.key} flex={1} display='flex' flexDirection='column'>
            <Typography variant='p2' color='grey/light' mb='xs'>
              {t(`${section.key}.title`)}
            </Typography>
            <Box display='flex' flexDirection='column' style={{ gap: 8 }}>
              {section.items.map(itemKey => (
                <Box key={itemKey} display='flex' flexDirection='row' alignItems='flex-start'>
                  <Box mr='xxs' mt='xxxs'>
                    <Svg name='check' fill='primary' size='xs' />
                  </Box>
                  <Typography variant='p2/light' flex={1}>
                    {t(`${section.key}.${itemKey}`)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
};
